/**
 * Step 0: Intake - requirement text AND the two upload boxes, on one screen.
 *
 * INTAKE COMPRESSION V1 (Ashok ruling, 2026-08-20). The old step 1
 * ("Uploads") is absorbed here. The gain is ordering, not screen count:
 * the parse now runs ONCE, after the BOQ and the documents exist, instead
 * of before them. memory/DESIGN-intake-state-model.md §2 traces every
 * defect class found 2026-07-29/30 to parsing before the evidence arrived.
 *
 * =====================================================================
 * THE MODE DISCRIMINATOR - the single most load-bearing line in the file
 * =====================================================================
 * This step is EDITABLE or READ-ONLY according to `hasParsedRequirement`,
 * which reads the DATA. Never status, never step index, never
 * isStepPreviouslyCompleted.
 *
 * isStepPreviouslyCompleted('intake') is `0 < furthestStepReached`. Before
 * the merge, a document at status "intake" resumed to step 1 (Uploads), so
 * intake sat behind the frontier and that test was true. After the merge it
 * resumes to step 0, `0 < 0` is false, and using it here would have
 * rendered an EMPTY EDITABLE requirement box for every one of the 89 live
 * documents at that status (72 staging, 17 prod - roughly 30% of both
 * databases), all of which carry a parse. That is the defect this file is
 * shaped to avoid, and StepIntake.modeDiscriminator.test.jsx pins it with
 * a draft and a parsed document AT THE SAME STATUS, so a component that
 * were always read-only could not pass either.
 *
 * =====================================================================
 * TWO RENDERED STATES, and NOTHING IS PERSISTED UNTIL CONTINUE
 * =====================================================================
 * (Ashok ruling, 2026-08-21 - this replaces the three-state shape this
 * file had for one day. The third state existed only because attaching a
 * file needed a procurement_id, so the buyer had to SAVE first and the
 * draft became a visible action with a serial attached to it. It is gone.)
 *
 *   editable  -> requirement box + BOTH upload boxes, live from the first
 *                render. A BOQ goes to the STATELESS preview endpoint for
 *                its rows and the File is held in wizard state; a document
 *                is held outright. No draft, no serial, no upload. One
 *                Continue then performs draft-create -> attach -> parse.
 *   read-only -> the parsed requirement, the read-only BOQ and document
 *                lists, RequirementContextCard, and a Continue that
 *                generates questions. Unlocking via the banner returns it
 *                to the editable shape WITH the upload boxes.
 *
 * A buyer who abandons the editable state leaves NOTHING behind. That is
 * only safe because held files are dirty state: see WizardContext's
 * hasUnsavedChanges, which ORs them in so the existing navigation warning
 * fires even when no requirement text has been typed.
 */
import { useState } from 'react';
import { useWizard, hasParsedRequirement } from './WizardContext';
import GenerationLock from './GenerationLock';
import { ReadOnlyStepBanner } from './WizardBanners';
import DownstreamResetConfirmDialog, { CATEGORY_REPLACE_VARIANT, INTAKE_EDIT_VARIANT } from './DownstreamResetConfirmDialog';
import { RETIRED_CATEGORY_REPLACED } from '@/lib/categoryGateDecisionContext';
import RequirementContextCard from './RequirementContextCard';
import FileUploadSection from '@/components/FileUploadSection';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { autoResizeTextarea, TEXTAREA_BASE_ROWS, GROWING_TEXTAREA_CLASS } from '@/lib/autoResizeTextarea';
import { REQUIREMENT_MIN_CHARS, REQUIREMENT_MAX_CHARS, requirementLengthState } from '@/lib/requirementLength';
import { Label } from '@/components/ui/label';
import { AiProcessingNotice } from '@/lib/aiProcessingNotices';
import { getCategoryConfidence, getCategoryNoFit, getCategoryNoFitSubject } from '@/components/ParsedRequirementFields';
import { AWAITING_MASTER_UPDATE, categoryNoFitRequestNumber } from '@/lib/categoryNoFit';
import { ArrowRight, Loader2, FileText, FileSpreadsheet } from 'lucide-react';

// SEQUENCED STAGE SETS, following StepLineItems.jsx's `pendingAction`
// pattern rather than inventing a second one. This step drives two
// different long operations from two different buttons, and showing
// "Generating questions..." during a parse (or vice versa) is the exact
// misleading-progress-text problem that pattern was introduced to fix.
//
// ONE LOCK FOR THE WHOLE CONTINUE, NOT THREE (Ashok ruling, 2026-08-21).
// Continue now performs draft-create, then an upload per held file, then
// the parse. Each is a separate request, and locking per request would
// flash the overlay up to three times for what the buyer experienced as
// one click. `loading` is set once by handleIntakeSubmit and cleared once
// in its finally, so the overlay appears once; these stages narrate the
// whole sequence, in its actual order, rather than any single call.
const CONTINUE_STAGES = [
  'Saving your requirement...',
  'Attaching your files...',
  'Reading your requirement...',
  'Classifying the procurement...',
  'Finalizing your requirement summary...',
];

const QUESTION_GENERATION_STAGES = [
  'Reviewing your requirement...',
  'Applying procurement policy...',
  'Generating questions...',
];

// LIVE CHARACTER COUNT AGAINST BOTH BOUNDS (Ruling 3, 2026-09-03). Always
// visible, not only near a limit: a buyer under the floor used to learn
// it only from the server's 400 at submit. Both bounds are NAMED in every
// state - the ceiling as the number the count is measured against, the
// floor as the minimum beside it - so a counter that showed only the
// ceiling (mutant AR7) reads differently from this one in every state.
// See lib/requirementLength.js for why the displayed number is the RAW
// length while the floor is judged on trimmed text; when the two verdicts
// disagree (whitespace padding), both notes render.
const RequirementLengthCounter = ({ value }) => {
  const { rawCount, underFloor, overCeiling } = requirementLengthState(value);
  const state = overCeiling && underFloor ? 'over-and-under' : overCeiling ? 'over' : underFloor ? 'under' : 'ok';
  const tone = overCeiling ? 'text-red-600' : underFloor ? 'text-amber-700' : 'text-zinc-500';
  return (
    <p
      className={`text-xs mt-1 flex flex-wrap gap-x-3 ${tone}`}
      data-testid="requirement-length-counter"
      data-state={state}
      aria-live="polite"
    >
      <span>
        {rawCount.toLocaleString('en-US')} / {REQUIREMENT_MAX_CHARS.toLocaleString('en-US')} characters
        {' · '}minimum {REQUIREMENT_MIN_CHARS}
      </span>
      {overCeiling && (
        <span data-testid="requirement-length-over">
          Over the {REQUIREMENT_MAX_CHARS.toLocaleString('en-US')}-character maximum - shorten before continuing.
        </span>
      )}
      {underFloor && (
        <span data-testid="requirement-length-under">
          At least {REQUIREMENT_MIN_CHARS} characters of text needed.
        </span>
      )}
    </p>
  );
};

const StepIntake = () => {
  const {
    loading,
    procurementId,
    userRequirement,
    setUserRequirement,
    parsedRequirement,
    boqItems,
    setBoqItems,
    boqSource,
    setBoqSource,
    attachedDocuments,
    setAttachedDocuments,
    categoryActiveResolvable,
    masterReferenceAdvisory,
    handleIntakeSubmit,
    heldBoqFile,
    setHeldBoqFile,
    heldDocuments,
    setHeldDocuments,
    getTempRequestId,
    handleIntakeEditSubmit,
    handleUploadsComplete,
    handleCategoryOverride,
    handleCategoryDeclareNoFit,
    isStepUnlocked,
    isStepPreviouslyCompleted,
    unlockStepForEdit,
    markStepDirty,
  } = useWizard();

  const [showEditConfirm, setShowEditConfirm] = useState(false);

  // A RETIRED CATEGORY REPLACED ON A DOCUMENT WITH DOWNSTREAM STEPS CASCADES,
  // AND THE BUYER CONFIRMS FIRST (Ashok ruling, option B, 2026-09-17). Until
  // now the override adopted the new header and marked nothing stale, so the
  // same buyer was refused again downstream (PROC-000353, 000355, 000310).
  // Regeneration destroys buyer-confirmed content, so it stays a deliberate
  // act: the picker's choice is HELD here, the shared confirmation opens, and
  // nothing is written until the buyer confirms.
  //
  // ONLY retired_category_replaced, ONLY with downstream steps. A voluntary
  // change of an ACTIVE category (the "change" link) and a low-signal pick do
  // not cascade - nothing was retired (mutant BV4). A retired header on a
  // document with nothing downstream has nothing to reset, so it writes
  // directly, exactly as before.
  const [pendingCategoryReplace, setPendingCategoryReplace] = useState(null);
  const confirmCategory = (code, decisionContext) => {
    if (decisionContext === RETIRED_CATEGORY_REPLACED && isStepPreviouslyCompleted('intake')) {
      // Resolved on confirm or cancel, so the picker's own "saving" state
      // spans the decision and releases either way.
      return new Promise((resolve) => setPendingCategoryReplace({ code, decisionContext, resolve }));
    }
    return handleCategoryOverride(code, decisionContext);
  };
  const confirmCategoryReplace = async () => {
    const pending = pendingCategoryReplace;
    if (!pending) return;
    setPendingCategoryReplace(null);
    // WRITE, THEN MARK: handleCategoryOverride marks downstream stale only
    // after update-intake succeeds, so a failed write leaves nothing stale.
    await handleCategoryOverride(pending.code, pending.decisionContext, { cascade: true });
    pending.resolve();
  };
  const cancelCategoryReplace = () => {
    const pending = pendingCategoryReplace;
    setPendingCategoryReplace(null);
    // Cancel writes nothing and marks nothing: the held choice is dropped.
    if (pending) pending.resolve();
  };
  // Which of the two long operations is running, so the lock shows the
  // right stage list. Never inferred from `loading` alone - that flag is
  // shared and cannot say which action set it.
  const [pendingAction, setPendingAction] = useState(null);

  const isParsed = hasParsedRequirement(parsedRequirement);
  const isUnlockedEdit = isStepUnlocked('intake');
  // READ-ONLY IS A DATA FACT, ONE UNLOCK AWAY. See the header block.
  const isReadOnly = isParsed && !isUnlockedEdit;
  // THERE IS NO LONGER ANY SUCH THING AS "CANNOT ATTACH YET" (Ashok
  // ruling, 2026-08-21). Both boxes are live from the first render of a
  // brand-new request: a BOQ goes to the stateless preview for its rows
  // and the File is held, a document is held outright, and NOTHING reaches
  // the server until Continue. The save-first step that used to stand
  // between the buyer and the upload boxes - and the draft it created just
  // to have somewhere to put a file - are both gone.

  // ===================================================================
  // THE CATEGORY GATE IS A POST-PARSE INTERSTITIAL, NOT A BUTTON
  // PRECONDITION (Ashok ruling, 2026-08-20)
  // ===================================================================
  // The two facts it keys on - a zero-floor confidence pick the buyer
  // never confirmed, and a code that no longer resolves to an active
  // master row - BOTH COME FROM THE PARSE. Under draft -> attach -> parse
  // the parse and the Continue that consumes its verdict collapse into one
  // click, so there is no longer a separate button-press for a
  // precondition to sit in front of. A disabled Continue would be the
  // buyer's first and only signal, arriving at the moment they expected to
  // move on, with the explanation somewhere else on the page.
  //
  // So when it fires, this step renders the DECISION instead of the
  // Continue: the picker, the reason, and no forward control at all until
  // it is answered. Continue is not disabled - it is not there. That is
  // the difference between "you may not proceed" and "here is the thing
  // to do", and it is why a disabled-button assertion is the wrong shape
  // for these tests.
  //
  // UNCHANGED IN SUBSTANCE: the OR, and the `=== false, NOT falsy` rule
  // (null means the master could not be read, and a gate demanding a
  // selection is the wrong answer to "we could not check" - the buyer
  // meets the honest 503 at Continue instead). Neither condition implies
  // the other; see RequirementContextCard, which must render the control
  // under exactly the same OR.
  //
  // It can only fire once a parse exists - there is no category to gate on
  // before then, and categoryActiveResolvable is null on a draft.
  // THE HEADER NO-FIT (F1 / C1b-1b) is the THIRD condition, and it must
  // appear in RequirementContextCard's `categoryNeedsSelection` too - the
  // two are a registered mirror pair (CLAUDE.md), not a coincidence. If
  // they disagree the buyer is either blocked with no control or offered a
  // control while free to walk past it.
  //
  // NO NEW REFUSAL IS BEING ADDED HERE, and that is the ruling. This step
  // renders the DECISION where Continue would be - Continue is not
  // disabled, it is not there - so keeping this gate open IS the park. It
  // is already stronger than anything at Lines or at generation.
  const categoryNoFit = getCategoryNoFit(parsedRequirement);
  const categorySelectionRequired =
    isParsed
    && (getCategoryConfidence(parsedRequirement) === 'none'
        || categoryActiveResolvable === false
        || Boolean(categoryNoFit));

  // ONE CONTINUE. For a new or partially-persisted request this is the
  // whole draft -> attach -> parse sequence (WizardContext's
  // handleIntakeSubmit); for a parsed procurement unlocked for edit it is
  // the in-place re-parse, unchanged. Either way it is one click and one
  // lock.
  const runContinue = () => {
    setPendingAction('parse');
    (isUnlockedEdit ? handleIntakeEditSubmit : handleIntakeSubmit)();
  };

  // STALE-QUESTION ROUTING, PRESERVED AND STRENGTHENED (ruled).
  //
  // The Uploads step's read-only Continue used to branch: it called the
  // generating handler when Questions was marked stale, and
  // plain-advanced (setCurrentStep) otherwise. The branch existed because
  // Questions can go stale without THIS step being touched - an upstream
  // requirement edit does it - and a stale Questions step must never be
  // skipped past uncached.
  //
  // Merged, this step's Continue IS the question-generation boundary, so it
  // always routes through the generating handler. The stale case is
  // therefore covered by construction rather than by remembering to check,
  // and the non-stale case costs nothing: generate-questions has its own
  // input-hash short-circuit and returns regenerated:false without an agent
  // call. Removing the routing entirely - a plain advance - is mutant I5,
  // killed by StepIntake.modeDiscriminator.test.jsx.
  const runQuestions = () => {
    setPendingAction('questions');
    handleUploadsComplete();
  };

  const lockStages = pendingAction === 'questions' ? QUESTION_GENERATION_STAGES : CONTINUE_STAGES;

  const uploadBoxes = (
    <FileUploadSection
      procurementId={procurementId}
      boqItems={boqItems}
      setBoqItems={setBoqItems}
      boqSource={boqSource}
      setBoqSource={setBoqSource}
      attachedDocuments={attachedDocuments}
      setAttachedDocuments={setAttachedDocuments}
      heldBoqFile={heldBoqFile}
      setHeldBoqFile={setHeldBoqFile}
      heldDocuments={heldDocuments}
      setHeldDocuments={setHeldDocuments}
      getTempRequestId={getTempRequestId}
    />
  );

  // The read-only BOQ / attached-documents pair, carried over verbatim in
  // substance from the Uploads step. The BOQ box is ALWAYS present even
  // when empty - an empty BOQ must not collapse this to a single lonely
  // Documents box (layout correction, retained).
  const readOnlyAttachments = (
    <div className="grid gap-5 md:grid-cols-2 items-start">
      <div className="bg-white border border-zinc-200 p-5 rounded-sm">
        <div className="flex items-center gap-3 mb-3">
          <FileSpreadsheet className="w-5 h-5 text-[#0055FF]" />
          <h3 className="text-sm font-semibold text-zinc-900">Bill of Quantities ({boqItems.length} items)</h3>
        </div>

        {/* WHICH FILE THIS CAME FROM (2026-08-21).
            A BOQ is replace-only - there is no delete - so a buyer who
            suspects the wrong spreadsheet was attached has no way to check
            without this. The attached-documents box beside it has shown
            filenames all along; this box showed a count and nothing else.
            NOT A REGRESSION: StepUploads.jsx never rendered boq_source in
            its read-only branch either (eccf8f6:StepUploads.jsx - boqSource
            was destructured at :46 and used at :225 ONLY, to feed
            FileUploadSection in the editable branch). Merging the steps put
            the two boxes side by side and made the asymmetry visible.

            SAME SENTENCE AS THE EDITABLE BRANCH, deliberately - see
            FileUploadSection.jsx's boq-provenance-line. One fact should
            read one way whichever mode the buyer is in, and
            StepIntake.boqFilename.test.jsx asserts the two produce
            identical text for identical input, so the duplicate cannot
            drift silently.

            ONE FACT, ONE PLACE (2026-08-21). This line read "Loaded from
            {filename} - {n} items" for a day, directly under a heading
            already reading "Bill of Quantities ({n} items)" - the count
            twice on one card. The heading keeps the count; this keeps the
            filename, which is the fact nothing else on the card carries.
            item_count is still in boq_source and is simply not rendered
            here.

            THE ABSENT ARM IS NOT DEFENSIVE PADDING. 4 staging documents
            carry boq_items with NO boq_source - uploaded before d0a3acf
            (2026-07-30) added the field. They must render the count alone,
            exactly as before, never an empty "Source:". Prod has none
            today, which is why the arm is asserted by test rather than
            trusted to a manual pass. */}
        {/* GUARDED ON NON-BLANK CONTENT, not on truthiness. A
            whitespace-only filename is a truthy string, so `?.filename &&`
            alone would render "Source:   " - a gap where the
            answer should be, which is worse than the count alone. Caught by
            test, not by review. (The editable copy guards on the OBJECT and
            would still render that gap; changing it is a non-goal here and
            is flagged in the report rather than fixed in passing.) */}
        {String(boqSource?.filename || '').trim() && (
          <p className="mb-3 text-xs text-zinc-500" data-testid="readonly-boq-provenance">
            Source: {boqSource.filename}
          </p>
        )}

        {boqItems.length === 0 ? (
          <p className="text-sm text-zinc-400 italic">No BOQ uploaded.</p>
        ) : (
          <div className="bg-zinc-50 border border-zinc-200 rounded-sm overflow-hidden max-h-48 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-zinc-100 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-zinc-700">#</th>
                  <th className="px-3 py-2 text-left font-medium text-zinc-700">Product Name</th>
                  <th className="px-3 py-2 text-left font-medium text-zinc-700">Qty</th>
                  <th className="px-3 py-2 text-left font-medium text-zinc-700">UoM</th>
                </tr>
              </thead>
              <tbody>
                {boqItems.map((item, idx) => (
                  <tr key={`ro-boq-${item.line_number ?? idx}`} className="border-t border-zinc-100">
                    <td className="px-3 py-2 text-zinc-500">{item.line_number}</td>
                    <td className="px-3 py-2 text-zinc-700">{item.product_name}</td>
                    <td className="px-3 py-2 text-zinc-600">{item.quantity}</td>
                    <td className="px-3 py-2 text-zinc-600">{item.uom}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white border border-zinc-200 p-5 rounded-sm">
        <div className="flex items-center gap-3 mb-3">
          <FileText className="w-5 h-5 text-[#0055FF]" />
          <h3 className="text-sm font-semibold text-zinc-900">Attached Documents ({attachedDocuments.length})</h3>
        </div>
        {attachedDocuments.length === 0 ? (
          <p className="text-sm text-zinc-400 italic">No documents attached.</p>
        ) : (
          <div className="space-y-2">
            {attachedDocuments.map((doc) => (
              <div key={doc.id} className="flex items-center gap-3 bg-zinc-50 border border-zinc-200 px-4 py-3 rounded-sm">
                <FileText className="w-4 h-4 text-zinc-500" />
                <span className="text-sm text-zinc-700">{doc.filename}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // =====================================================================
  // READ-ONLY: a parsed procurement being revisited or resumed
  // =====================================================================
  if (isReadOnly) {
    return (
      <div className="relative" data-testid="intake-step-readonly">
        <GenerationLock active={loading} stages={QUESTION_GENERATION_STAGES} />

        {/* allowCategoryChange: Intake is the ONLY step that opts in -
            nothing downstream has been generated from the category yet, so
            a change here has no un-cascaded effect. That reasoning is
            unchanged by the merge; the prop moved with the card. See its
            declaration in RequirementContextCard.jsx.

            suppressRequirementEcho: Intake is also the only step that opts
            in to THIS, and for a reason the merge created. The card's
            Box 1 echoes the requirement; on Questions and Answers that
            text was typed on an earlier step and the echo is the only way
            to see it without walking back. Here it is on the same screen,
            in "Your Requirement" below, so the echo is a straight
            duplicate. The card's PARSED DETAILS - category and its change
            link, quantity, procurement type, scope, objective - are
            untouched; only the repeated paragraph goes. */}
        <RequirementContextCard
          procurementId={procurementId}
          userRequirement={userRequirement}
          parsedRequirement={parsedRequirement}
          categoryActiveResolvable={categoryActiveResolvable}
          masterReferenceAdvisory={masterReferenceAdvisory}
          onCategoryConfirm={confirmCategory}
          onCategoryDeclareNoFit={handleCategoryDeclareNoFit}
          allowCategoryChange
          suppressRequirementEcho
        />

        <div className="mb-5">
          <h2 className="text-2xl font-bold text-zinc-900 mb-2">
            Your Requirement
          </h2>
          <p className="text-zinc-600">
            The requirement and any supporting files used to analyse this request.
          </p>
        </div>

        <ReadOnlyStepBanner
          stepId="intake"
          onEdit={() => setShowEditConfirm(true)}
        />

        <div className="space-y-6">
          <div>
            <Label className="text-sm font-medium text-zinc-700 mb-2 block">
              Procurement Requirement
            </Label>
            {/* SIZES TO ITS CONTENT. This carried min-h-[180px], inherited
                from the editable Textarea it mirrors - which is the right
                height for TYPING and the wrong one for READING: a
                one-sentence requirement sat above six empty lines. The
                editable branch keeps its own min-height, unchanged. */}
            <div
              className="bg-zinc-50 border border-zinc-200 rounded-sm px-3 py-2 text-sm text-zinc-700 whitespace-pre-wrap"
              data-testid="requirement-readonly"
            >
              {userRequirement}
            </div>
          </div>

          {readOnlyAttachments}
        </div>

        {categorySelectionRequired ? (
          // THE INTERSTITIAL. It stands where Continue would be, so the
          // buyer's eye lands on the decision rather than on a dead
          // control. The picker itself is rendered by
          // RequirementContextCard above, under the same OR - this block
          // says WHY it is there and withholds forward movement while it
          // is unanswered.
          <div
            className="mt-6 border border-amber-300 bg-amber-50 rounded-sm p-4"
            data-testid="category-interstitial"
          >
            {/* THREE REASONS, THREE SENTENCES, and the no-fit one is
                checked FIRST. Declaring a no-fit clears the decided
                category, so the "no longer active" branch would otherwise
                claim the buyer's own declaration was a stale master row -
                and the "could not tell" branch would claim the analysis
                failed when in fact it succeeded and the answer was that
                nothing fits. Both are the wrong instruction, and the
                second is the specific false instruction Ruling 3 exists to
                prevent. */}
            <p className="text-sm font-semibold text-amber-900 mb-1">
              {categoryNoFit
                ? AWAITING_MASTER_UPDATE
                : 'Confirm the spend category before continuing'}
            </p>
            <p className="text-sm text-amber-800">
              {(() => {
                if (categoryNoFit) {
                  const number = categoryNoFitRequestNumber(getCategoryNoFitSubject(parsedRequirement));
                  return `Your request for a new spend category${number ? ` (${number})` : ''} is with the category administrator. This request cannot continue until the category master is updated, or until you select an existing category above.`;
                }
                return getCategoryConfidence(parsedRequirement) === 'none'
                  ? 'The analysis could not tell which spend category this request belongs to. Choose one above to continue.'
                  : 'The spend category on this request is no longer active in the category master. Choose a current one above to continue.';
              })()}
            </p>
          </div>
        ) : (
          <div className="flex justify-end mt-6">
            <Button
              onClick={runQuestions}
              disabled={loading}
              className="bg-[#0055FF] hover:bg-[#0044CC] text-white px-6"
              data-testid="continue-readonly-intake-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
              Continue
            </Button>
          </div>
        )}

        <DownstreamResetConfirmDialog
          open={showEditConfirm}
          variant={INTAKE_EDIT_VARIANT}
          onConfirm={() => {
            unlockStepForEdit('intake', { userRequirement, attachedDocuments, boqItems });
            setShowEditConfirm(false);
          }}
          onCancel={() => setShowEditConfirm(false)}
        />

        {/* THE SAME DIALOG, for a retired category replaced on a document
            with downstream steps (Ashok ruling, option B, 2026-09-17). */}
        <DownstreamResetConfirmDialog
          open={Boolean(pendingCategoryReplace)}
          variant={CATEGORY_REPLACE_VARIANT}
          onConfirm={confirmCategoryReplace}
          onCancel={cancelCategoryReplace}
        />
      </div>
    );
  }

  // =====================================================================
  // EDITABLE: a new request, a draft, or a parsed one unlocked for edit
  // =====================================================================
  return (
    <div className="relative" data-testid="intake-step-editable">
      <GenerationLock active={loading} stages={lockStages} />

      <div className="mb-5">
        <h2 className="text-2xl font-bold text-zinc-900 mb-2">
          Describe Your Procurement Need
        </h2>
        <p className="text-zinc-600">
          Enter your procurement requirement in detail. You may also attach a BOQ
          or supporting documents like a scope of work, specifications or designs -
          they are read as part of the same analysis.
        </p>
        <AiProcessingNotice className="italic mt-1" />
      </div>

      <div className="space-y-6">
        <div>
          <Label htmlFor="requirement" className="text-sm font-medium text-zinc-700 mb-2 block">
            Procurement Requirement
          </Label>
          {/* GROWS WITH ITS CONTENT (Ruling C, 2026-09-03; base and ceiling
              per item 8 the same day): the shared autoResizeTextarea helper,
              wired exactly as StepQuestions and the Lines editor wire it -
              ref callback for text arriving from state on mount, onChange
              for every keystroke. Starts at TEXTAREA_BASE_ROWS, grows to
              the shared ceiling class, then scrolls inside itself. The
              growth itself reads scrollHeight and is browser-only. */}
          <Textarea
            id="requirement"
            ref={autoResizeTextarea}
            rows={TEXTAREA_BASE_ROWS}
            value={userRequirement}
            onChange={(e) => {
              setUserRequirement(e.target.value);
              autoResizeTextarea(e.target);
              markStepDirty('intake');
            }}
            placeholder="Example: We need 50 Dell laptops for IT department, delivery to Gurgaon office by April 2026. Also need 1 year on-site support service."
            className={`${GROWING_TEXTAREA_CLASS} bg-zinc-50 border-zinc-200 focus:border-[#0055FF] focus:ring-1 focus:ring-[#0055FF]`}
            data-testid="requirement-input"
          />
          <RequirementLengthCounter value={userRequirement} />
        </div>

        {/* ALWAYS PRESENT, from the first render. Nothing has to be saved
            to reach them. */}
        {uploadBoxes}

        <div className="flex justify-end">
          {/* GATE THE CONTINUE, NOT THE UPLOAD BUTTONS (ruled). A buyer may
              attach a BOQ first and type the requirement afterwards - a
              natural order when the spreadsheet is what prompted the
              request - so the only client-side gate is here, at the one
              moment the text is actually needed, and not as a precondition
              on picking a file. WHAT THIS GATE CHECKS IS NON-BLANK ONLY.
              Neither the 10-character floor nor the 5,000-character ceiling
              is enforced in the browser; both live server-side in
              _validate_requirement_text (routes/procurement.py), applied by
              draft-create, parse and update-intake, each refusing with a
              400. The counter under the textarea is the client-side CUE for
              both bounds and enforces nothing (Ruling 3, 2026-09-03). An
              earlier version of this comment claimed the 10-5000 rule was
              enforced here; it never was - corrected 2026-09-03. */}
          <Button
            onClick={runContinue}
            disabled={loading || !userRequirement.trim()}
            className="bg-[#0055FF] hover:bg-[#0044CC] text-white px-6"
            data-testid="submit-intake-btn"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <ArrowRight className="w-4 h-4 mr-2" />
            )}
            {loading ? 'Processing...' : 'Continue'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default StepIntake;
